import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';

import { apiPaths } from '../../../service/apiPaths';
import ApiService from '../../../service/ApiService';
import { Roles } from '../../../util/constant';

interface WeekData {
  weekId: string;
  weekName: string;
  startDate: string;
  endDate: string;
  startDay: string;
  endDay: string;
  hasLeads: boolean;
  hasTargets: boolean;
  totalLeads: number;
  totalTargets: number;
}

interface AvailableWeeksResponse {
  totalWeeks: number;
  weeks: WeekData[];
}

interface DailyBreakdown {
  date: string;
  dayName: string;
  target: number;
  achieved: number;
  validLeads: number;
  totalLeads: number;
  excludedLeads: number;
  isTargetMet: boolean;
}

interface UserPerformance {
  userId: string;
  userName: string;
  userEmail: string;
  userRole: string;
  userSubRole: string;
  weeklyTarget: number;
  weeklyAchieved: number;
  weeklyExcluded: number;
  validLeads: number;
  totalLeads: number;
  excludedLeads: number;
  targetCompletionRate: string;
  dailyBreakdown: DailyBreakdown[];
}

interface WeeklyPerformanceResponse {
  weekRange: {
    startDate: string;
    endDate: string;
    startDay: string;
    endDay: string;
  };
  totalUsers: number;
  data: UserPerformance[];
}

interface MyWeeklyPerformanceResponse {
  weekRange: {
    startDate: string;
    endDate: string;
    startDay: string;
    endDay: string;
  };
  weeklyTarget: number;
  weeklyAchieved: number;
  validLeads: number;
  totalLeads: number;
  excludedLeads: number;
  targetCompletionRate: string;
  dailyBreakdown: DailyBreakdown[];
}

interface TodayTargetResponse {
  hasTarget: boolean;
  target?: {
    dailyTarget: number;
    achievedLeads: number;
    excludedLeads: number;
    totalLeadsCreated: number;
    isTargetMet: boolean;
    notes?: string;
  };
  todayStats: {
    validLeads: number;
    totalLeads: number;
    excludedLeads: number;
  };
  progress: {
    percentage: string;
    remaining: number;
  };
}

interface SetTargetRequest {
  userId: string;
  targetDate: string;
  dailyTarget: number;
  notes?: string;
}

const useGamification = () => {
  const [availableWeeks, setAvailableWeeks] = useState<WeekData[]>([]);
  const [selectedWeek, setSelectedWeek] = useState<string>('');
  const [weeklyPerformance, setWeeklyPerformance] = useState<WeeklyPerformanceResponse | null>(null);
  const [myWeeklyPerformance, setMyWeeklyPerformance] = useState<MyWeeklyPerformanceResponse | null>(null);
  const [todayTarget, setTodayTarget] = useState<TodayTargetResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [targetModalOpen, setTargetModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<string>('');
  const [dailyTarget, setDailyTarget] = useState<number>(0);
  const [targetNotes, setTargetNotes] = useState<string>('');
  const [targetDate, setTargetDate] = useState<string>('');

  const role = localStorage.getItem('role');
  const currentSubRole = localStorage.getItem('subRole');
  const isAdmin = role === Roles.admin || currentSubRole === Roles.bde_manager;

  // Get available weeks
  const fetchAvailableWeeks = async (limit: number = 12) => {
    setLoading(true);
    setError(null);
    try {
      const endpoint = isAdmin 
        ? `${apiPaths.getAvailableWeeks}?limit=${limit}`
        : `${apiPaths.getMyAvailableWeeks}?limit=${limit}`;
      
      const response = await ApiService({
        method: 'GET',
        endpoint,
      });
      
      setAvailableWeeks(response.weeks || []);
      if (response.weeks && response.weeks.length > 0 && !selectedWeek) {
        setSelectedWeek(response.weeks[0].weekId);
      }
    } catch (error: any) {
      console.error('Error fetching available weeks:', error);
      setError(error?.response?.data?.error || 'Failed to fetch available weeks');
      toast.error(error?.response?.data?.error || 'Failed to fetch available weeks');
    } finally {
      setLoading(false);
    }
  };

  // Get weekly performance
  const fetchWeeklyPerformance = async (week?: string) => {
    if (!week) return;
    
    setLoading(true);
    setError(null);
    try {
      const endpoint = isAdmin 
        ? `${apiPaths.getWeeklyPerformance}?week=${week}`
        : `${apiPaths.getMyWeeklyPerformance}?week=${week}`;
      
      const response = await ApiService({
        method: 'GET',
        endpoint,
      });
      
      if (isAdmin) {
        setWeeklyPerformance(response);
      } else {
        setMyWeeklyPerformance(response);
      }
    } catch (error: any) {
      console.error('Error fetching weekly performance:', error);
      setError(error?.response?.data?.error || 'Failed to fetch weekly performance');
      toast.error(error?.response?.data?.error || 'Failed to fetch weekly performance');
    } finally {
      setLoading(false);
    }
  };

  // Get today's target
  const fetchTodayTarget = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await ApiService({
        method: 'GET',
        endpoint: apiPaths.getTodayTarget,
      });
      
      setTodayTarget(response);
    } catch (error: any) {
      console.error('Error fetching today\'s target:', error);
      setError(error?.response?.data?.error || 'Failed to fetch today\'s target');
      toast.error(error?.response?.data?.error || 'Failed to fetch today\'s target');
    } finally {
      setLoading(false);
    }
  };

  // Set daily target (admin only)
  const setDailyTargetForUser = async (data: SetTargetRequest) => {
    if (!isAdmin) {
      toast.error('Only admins can set targets');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await ApiService({
        method: 'POST',
        endpoint: apiPaths.setDailyTarget,
        data,
      });
      
      toast.success('Daily target set successfully');
      setTargetModalOpen(false);
      setSelectedUser('');
      setDailyTarget(0);
      setTargetNotes('');
      setTargetDate('');
      
      // Refresh data
      if (selectedWeek) {
        fetchWeeklyPerformance(selectedWeek);
      }
      
      return response;
    } catch (error: any) {
      console.error('Error setting daily target:', error);
      setError(error?.response?.data?.error || 'Failed to set daily target');
      toast.error(error?.response?.data?.error || 'Failed to set daily target');
    } finally {
      setLoading(false);
    }
  };

  // Handle week change
  const handleWeekChange = (weekId: string) => {
    setSelectedWeek(weekId);
    fetchWeeklyPerformance(weekId);
  };

  // Handle target modal
  const openTargetModal = (userId: string, date: string) => {
    setSelectedUser(userId);
    setTargetDate(date);
    setTargetModalOpen(true);
  };

  const closeTargetModal = () => {
    setTargetModalOpen(false);
    setSelectedUser('');
    setDailyTarget(0);
    setTargetNotes('');
    setTargetDate('');
    setError(null);
  };

  const handleSetTarget = () => {
    if (!selectedUser || !targetDate || dailyTarget <= 0) {
      toast.error('Please fill all required fields');
      return;
    }

    setDailyTargetForUser({
      userId: selectedUser,
      targetDate,
      dailyTarget,
      notes: targetNotes || undefined,
    });
  };

  // Clear error
  const clearError = () => {
    setError(null);
  };

  // Initialize data
  useEffect(() => {
    fetchAvailableWeeks();
    fetchTodayTarget();
  }, []);

  useEffect(() => {
    if (selectedWeek) {
      fetchWeeklyPerformance(selectedWeek);
    }
  }, [selectedWeek]);

  return {
    // State
    availableWeeks,
    selectedWeek,
    weeklyPerformance,
    myWeeklyPerformance,
    todayTarget,
    loading,
    error,
    targetModalOpen,
    selectedUser,
    dailyTarget,
    targetNotes,
    targetDate,
    isAdmin,

    // Actions
    handleWeekChange,
    openTargetModal,
    closeTargetModal,
    handleSetTarget,
    setDailyTarget,
    setTargetNotes,
    setTargetDate,
    fetchTodayTarget,
    clearError,
  };
};

export default useGamification; 